import React, { useLayoutEffect, useRef, useState } from 'react';
import { List, CheckSquare } from 'lucide-react';

type ListTextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  /** Glas-Tooltips (data-tip) und Token-Farben statt nativer title – für Bereiche im neuen Design mit GlasTooltipSchicht */
  glasTips?: boolean;
  /** Wächst mit dem Text bis zu dieser Höhe (CSS-Wert, z. B. 'min(45vh, 380px)'), danach scrollt es (#535). */
  wachsenBis?: string;
};

const ListTextarea: React.FC<ListTextareaProps> = ({
  onKeyDown,
  onChange,
  onFocus,
  onBlur,
  className,
  rows = 2,
  style,
  glasTips = false,
  wachsenBis,
  ...restProps
}) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Mitwachsen: Höhe an den Inhalt anpassen; max-height begrenzt, darüber scrollt das Feld.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!wachsenBis || !el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [wachsenBis, restProps.value, isFocused]);

  const numRows = Number(rows) || 2;
  const lineHeight = 22;
  const collapsedMinHeight = numRows * lineHeight;
  const expandedMinHeight = Math.max(numRows, 5) * lineHeight;

  const updateValue = (newValue: string) => {
    const el = ref.current;
    if (!el || !onChange) return;
    el.value = newValue;
    onChange({ target: el } as React.ChangeEvent<HTMLTextAreaElement>);
  };

  const insertBulletAtCurrentLine = () => {
    const el = ref.current;
    if (!el) return;
    const { value, selectionStart } = el;
    if (selectionStart === null) return;
    const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
    const newValue = value.slice(0, lineStart) + '• ' + value.slice(lineStart);
    updateValue(newValue);
    const newPos = selectionStart + 2;
    setTimeout(() => el.setSelectionRange(newPos, newPos), 0);
    el.focus();
  };

  const insertCheckboxAtCurrentLine = () => {
    const el = ref.current;
    if (!el) return;
    const { value, selectionStart } = el;
    if (selectionStart === null) return;
    const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
    const newValue = value.slice(0, lineStart) + '☐ ' + value.slice(lineStart);
    updateValue(newValue);
    const newPos = selectionStart + 2;
    setTimeout(() => el.setSelectionRange(newPos, newPos), 0);
    el.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const el = ref.current;
    if (!el) { onKeyDown?.(e); return; }
    const { value, selectionStart } = el;
    if (selectionStart === null) { onKeyDown?.(e); return; }

    // `-` + Leertaste am Zeilenanfang → `• `
    if (e.key === ' ') {
      const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
      const textBeforeCursor = value.slice(lineStart, selectionStart);
      if (textBeforeCursor === '-') {
        e.preventDefault();
        const newValue = value.slice(0, lineStart) + '• ' + value.slice(selectionStart);
        updateValue(newValue);
        setTimeout(() => el.setSelectionRange(lineStart + 2, lineStart + 2), 0);
        onKeyDown?.(e);
        return;
      }
      if (textBeforeCursor === '[') {
        e.preventDefault();
        const newValue = value.slice(0, lineStart) + '☐ ' + value.slice(selectionStart);
        updateValue(newValue);
        setTimeout(() => el.setSelectionRange(lineStart + 2, lineStart + 2), 0);
        onKeyDown?.(e);
        return;
      }
    }

    // Shift+Enter auf Bullet-Zeile → neuer Bullet-Punkt
    if (e.key === 'Enter' && e.shiftKey) {
      const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
      const lineContent = value.slice(lineStart, selectionStart);

      if (lineContent === '• ') {
        e.preventDefault();
        const newValue = value.slice(0, lineStart) + value.slice(selectionStart);
        updateValue(newValue);
        setTimeout(() => el.setSelectionRange(lineStart, lineStart), 0);
        onKeyDown?.(e);
        return;
      }

      if (lineContent.startsWith('• ')) {
        e.preventDefault();
        const newValue =
          value.slice(0, selectionStart) + '\n• ' + value.slice(selectionStart);
        updateValue(newValue);
        setTimeout(() => el.setSelectionRange(selectionStart + 3, selectionStart + 3), 0);
        onKeyDown?.(e);
        return;
      }
    }

    onKeyDown?.(e);
  };

  return (
    <div className="relative">
      <div className="absolute top-2 right-2 flex gap-0.5 z-10">
        <button
          type="button"
          tabIndex={-1}
          {...(glasTips ? { 'data-tip': 'Liste – Aufzählungspunkt einfügen', 'aria-label': 'Aufzählungspunkt einfügen', style: { color: 'var(--ink3)' } } : { title: 'Aufzählungsliste' })}
          onMouseDown={(e) => { e.preventDefault(); insertBulletAtCurrentLine(); }}
          className="p-1 text-gray-300 hover:text-[#c4a77d] transition-colors"
        >
          <List size={14} />
        </button>
        <button
          type="button"
          tabIndex={-1}
          {...(glasTips ? { 'data-tip': 'Checkliste – Kästchen zum Abhaken einfügen', 'aria-label': 'Kästchen einfügen', style: { color: 'var(--ink3)' } } : { title: 'Checkbox' })}
          onMouseDown={(e) => { e.preventDefault(); insertCheckboxAtCurrentLine(); }}
          className="p-1 text-gray-300 hover:text-[#c4a77d] transition-colors"
        >
          <CheckSquare size={14} />
        </button>
      </div>
      <textarea
        ref={ref}
        rows={rows}
        onKeyDown={handleKeyDown}
        onChange={onChange}
        onFocus={(e) => { setIsFocused(true); onFocus?.(e); }}
        onBlur={(e) => { setIsFocused(false); onBlur?.(e); }}
        style={{
          ...style,
          minHeight: `${isFocused ? expandedMinHeight : collapsedMinHeight}px`,
          transition: 'min-height 0.2s ease-in-out',
          ...(wachsenBis ? { maxHeight: wachsenBis, overflowY: 'auto' as const } : {}),
        }}
        className={[className, 'pr-14'].filter(Boolean).join(' ')}
        {...restProps}
      />
    </div>
  );
};

export default ListTextarea;
